import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { finishOfferedByStatus, offersPurchase, useProStatus } from '../../api/proStatus'
import { useSupabaseToken } from '../../hooks/useSupabaseToken'
import { trackEvent } from '../../lib/analytics'
import { purchaseSurface } from '../../lib/revenuecat'
import Paywall from '../pro/Paywall'
import { goProLabel } from '../pro/proCopy'
import AuthModal from './AuthModal'
import LanguageSwitcher from '../layout/LanguageSwitcher'
import { useCommonT } from '../../i18n'
import ModalShell from '../shared/ModalShell'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'

export default function AuthButton() {
  const { user, loading, signOut } = useAuth()
  const { toast } = useToast()
  const t = useCommonT()
  const { pathname, search } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [paywallOpen, setPaywallOpen] = useState(false)
  // Sign-out found deck edits it could not save: ask before leaving them.
  const [unsavedConfirm, setUnsavedConfirm] = useState(false)
  // Offline contract C3 step 4: unsynced changes exist and sign-out removes
  // them; the user answers here (count shown).
  const [c3Ask, setC3Ask] = useState<{ count: number; resolve: (go: boolean) => void } | null>(null)
  const { userId, token } = useSupabaseToken()
  const { data: proStatus } = useProStatus()
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close menu when clicking outside, plus Esc-to-close with focus return
  // (same disclosure semantics as NavMenu's dropdown).
  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  // Close on any route change: the Account settings Link already closes via
  // onClick; this covers Back/Forward, programmatic navigation, and
  // query-only navigation landing under an open menu.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMenuOpen(false)
  }, [pathname, search])

  if (loading) return null

  const handleSignOut = async (force = false) => {
    setMenuOpen(false)
    setUnsavedConfirm(false)
    const result = await signOut({
      ...(force ? { force: true } : {}),
      confirm: (count: number) => new Promise<boolean>(resolve => setC3Ask({ count, resolve })),
    })
    // Kept the account at the warning: nothing happened.
    if (result?.cancelled) return
    if (result?.blocked) {
      setUnsavedConfirm(true)
      return
    }
    // Signed out in this tab, but the browser kept the stored session
    // (offline and storage refused the removal): say so instead of "Signed out".
    if (result?.failed) {
      // eslint-disable-next-line i18next/no-literal-string -- toast tone code
      toast(t('auth.sign_out_failed'), 'error')
      return
    }
    // Explicit confirmation. Pre-2026-05-09 the only signal a sign-out
    // had succeeded was the button text changing to "Sign in" - fine
    // for power users, confusing for first-timers who weren't sure
    // anything happened.
    // eslint-disable-next-line i18next/no-literal-string -- toast tone code
    toast(t('auth.signed_out'), 'success')
  }

  if (user) {
    const displayName = user.user_metadata?.full_name || user.user_metadata?.name || user.email || ''
    return (
      <div ref={menuRef} className="relative">
        <button
          ref={buttonRef}
          onClick={() => setMenuOpen(o => !o)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          {user.user_metadata?.avatar_url ? (
            <img
              src={user.user_metadata.avatar_url}
              alt=""
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-purple-500/30 flex items-center justify-center text-xs text-white">
              {(displayName[0] || '?').toUpperCase()}
            </div>
          )}
          <span className="text-xs text-gray-400">{t('auth.account')}</span>
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-lg overflow-hidden z-50">
            {displayName && (
              <div className="px-3 py-2 border-b border-gray-800 text-xs text-gray-400 truncate">
                {displayName}
              </div>
            )}
            {/* FIRST below the header, and deliberately above Account
                settings: this menu is on every page, and until now the only
                two routes to the paywall were three levels into /account or
                behind an analyzed deck and a sent chat message. Same
                server-gated rule as the card it shortcuts, so an unread
                entitlement sells nothing. */}
            {offersPurchase(proStatus) && purchaseSurface() !== 'none' && (
              <button
                type="button"
                data-testid="account-menu-go-pro"
                onClick={() => {
                  setMenuOpen(false)
                  trackEvent('pro_paywall_opened', { trigger: 'account_menu' })
                  setPaywallOpen(true)
                }}
                className="block w-full text-left px-3 py-2 text-sm font-medium text-purple-200 hover:bg-gray-800 transition-colors"
              >
                {goProLabel()}
              </button>
            )}
            <Link
              to="/account"
              onClick={() => setMenuOpen(false)}
              className="block px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 transition-colors"
            >
              {t('auth.account_settings')}
            </Link>
            {/* Interface language. Renders nothing unless VITE_I18N is on. */}
            <LanguageSwitcher className="w-full px-3 py-2" />
            <button
              onClick={() => void handleSignOut()}
              className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 transition-colors border-t border-gray-800"
            >
              {t('auth.sign_out')}
            </button>
          </div>
        )}
        {c3Ask && (
          <ModalShell
            onClose={() => { c3Ask.resolve(false); setC3Ask(null) }}
            label={t('offline.signout_warning', { count: c3Ask.count })}
            className={`fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 outline-none ${OVERLAY_ABOVE_AD}`}
          >
            <div data-testid="signout-offline-confirm" className={`surface p-6 max-w-md w-full space-y-4 ${PANEL_FITS_OVERLAY}`}>
              <p className="text-sm text-gray-200">{t('offline.signout_warning', { count: c3Ask.count })}</p>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => { c3Ask.resolve(false); setC3Ask(null) }} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200">
                  {t('offline.signout_cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => { c3Ask.resolve(true); setC3Ask(null) }}
                  className="px-4 py-2 bg-red-900/60 text-red-200 text-sm font-medium rounded-lg hover:bg-red-900 transition-colors"
                >
                  {t('offline.signout_confirm')}
                </button>
              </div>
            </div>
          </ModalShell>
        )}
        {unsavedConfirm && (
          <ModalShell
            onClose={() => setUnsavedConfirm(false)}
            label={t('auth.unsaved_edits.body')}
            className={`fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 outline-none ${OVERLAY_ABOVE_AD}`}
          >
            <div data-testid="signout-unsaved-confirm" className={`surface p-6 max-w-md w-full space-y-4 ${PANEL_FITS_OVERLAY}`}>
              <p className="text-sm text-gray-200">{t('auth.unsaved_edits.body')}</p>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setUnsavedConfirm(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200">
                  {t('auth.unsaved_edits.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleSignOut(true)}
                  className="px-4 py-2 bg-red-900/60 text-red-200 text-sm font-medium rounded-lg hover:bg-red-900 transition-colors"
                >
                  {t('auth.unsaved_edits.confirm')}
                </button>
              </div>
            </div>
          </ModalShell>
        )}
        {/* Outside the `menuOpen` block on purpose - the click that opens the
            paywall also closes the menu, and a modal mounted inside it would
            unmount in the same tick. */}
        <Paywall
          proMonthlyMessages={proStatus?.pro_cap_month}
          finishOffered={finishOfferedByStatus(proStatus)}
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          userId={userId}
          token={token}
          isPro={false}
          // eslint-disable-next-line i18next/no-literal-string -- entry code
          entry="account_menu"
        />
      </div>
    )
  }

  return (
    <>
      <button
        onClick={() => setAuthModalOpen(true)}
        className="text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-lg px-3 py-1.5 transition-colors"
      >
        {t('auth.sign_in')}
      </button>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  )
}
